import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, Button, TextField, MenuItem, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  InputAdornment, Badge,
} from '@mui/material';
import { Inbox, Check, Close, Warning, Error as ErrIcon, Search, Refresh } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const CATEGORY_CFG: Record<string, { label: string; color: string; emoji: string }> = {
  time_off:   { label: 'Time Off',    color: '#1565c0', emoji: '🏖️' },
  finance:    { label: 'Finance',     color: '#2e7d32', emoji: '💰' },
  attendance: { label: 'Attendance',  color: '#e65100', emoji: '📅' },
  hr_admin:   { label: 'HR Admin',    color: '#6a1b9a', emoji: '🔧' },
  recruitment:{ label: 'Recruitment', color: '#00838f', emoji: '🎯' },
};

const TYPE_EMOJI: Record<string, string> = {
  leave: '🏖️', overtime: '⏱️', wfh: '🏠', expense: '🧾', toil: '⏳',
  business_trip: '✈️', leave_encashment: '💰', attendance_correction: '📋',
  shift_swap: '🔄', org_change: '🔄', requisition: '📝',
};

const URGENCY_CFG = {
  critical: { color: '#b71c1c', bg: '#ffebee', icon: <ErrIcon sx={{ fontSize: 14 }} />,   label: 'Critical' },
  high:     { color: '#e65100', bg: '#fff3e0', icon: <Warning  sx={{ fontSize: 14 }} />,   label: 'High'     },
  normal:   { color: '#546e7a', bg: '#eceff1', icon: null,                                  label: 'Normal'   },
};

interface Item {
  id: string; type: string; typeLabel: string; category: string;
  employeeId: string; employeeName: string; deptName: string;
  summary: string; detail?: string; amount?: number; currency?: string;
  requestedAt: string; daysWaiting: number; urgency: 'normal' | 'high' | 'critical';
  approveEndpoint: string; rejectEndpoint: string;
}

export default function ApprovalsHub() {
  const { user } = useAuth();
  const [items, setItems]   = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [urgFilter, setUrgFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<Item | null>(null);
  const [rejectNote, setRejectNote]     = useState('');
  const [successMsg, setSuccessMsg]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await apiFetch('/api/approvals-hub');
      setItems(d ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (item: Item) => {
    setProcessing(item.id);
    try {
      await apiFetch(item.approveEndpoint, { method: 'PATCH', body: JSON.stringify({}) });
      setSuccessMsg(`✅ Approved: ${item.typeLabel} for ${item.employeeName}`);
      setItems(p => p.filter(i => i.id !== item.id));
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (e: any) { setError(e.message); }
    finally { setProcessing(null); }
  };

  const reject = async () => {
    if (!rejectDialog) return;
    setProcessing(rejectDialog.id);
    try {
      await apiFetch(rejectDialog.rejectEndpoint, { method: 'PATCH', body: JSON.stringify({ reason: rejectNote }) });
      setSuccessMsg(`❌ Rejected: ${rejectDialog.typeLabel} for ${rejectDialog.employeeName}`);
      setItems(p => p.filter(i => i.id !== rejectDialog.id));
      setRejectDialog(null); setRejectNote('');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (e: any) { setError(e.message); }
    finally { setProcessing(null); }
  };

  const filtered = items.filter(i => {
    if (catFilter  && i.category !== catFilter)   return false;
    if (urgFilter  && i.urgency  !== urgFilter)   return false;
    if (typeFilter && i.type     !== typeFilter)  return false;
    if (search) {
      const q = search.toLowerCase();
      return i.employeeName?.toLowerCase().includes(q) ||
             i.summary?.toLowerCase().includes(q) ||
             i.deptName?.toLowerCase().includes(q);
    }
    return true;
  });

  const critical = items.filter(i => i.urgency === 'critical').length;
  const high     = items.filter(i => i.urgency === 'high').length;

  const uniqueTypes = [...Object.entries(
    items.reduce((acc: Record<string, string>, i) => { acc[i.type] = i.typeLabel; return acc; }, {})
  )].sort(([,a],[,b]) => a.localeCompare(b));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge badgeContent={items.length} color="error" max={99}>
            <Avatar sx={{ bgcolor: '#C08A2E', width: 48, height: 48 }}><Inbox /></Avatar>
          </Badge>
          <Box>
            <Typography variant="h5" fontWeight={700}>Approvals Hub</Typography>
            <Typography variant="body2" color="text.secondary">
              All pending approvals across leave, expenses, OT, WFH, TOIL, trips and more
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading}><Refresh /></IconButton>
        </Tooltip>
      </Box>

      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
      {error      && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip icon={<Inbox />} label={`${items.length} Pending`} color={items.length > 0 ? 'default' : 'default'} />
        {critical > 0 && <Chip icon={<ErrIcon />} label={`${critical} Critical (≥5 days)`} sx={{ bgcolor: '#ffebee', color: '#b71c1c' }} />}
        {high     > 0 && <Chip icon={<Warning  />} label={`${high} High (3–4 days)`}       sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />}
        {Object.entries(CATEGORY_CFG).map(([k, v]) => {
          const cnt = items.filter(i => i.category === k).length;
          if (!cnt) return null;
          return (
            <Chip key={k} label={`${v.emoji} ${v.label}: ${cnt}`}
              sx={{ bgcolor: v.color + '15', color: v.color }} size="small" />
          );
        })}
      </Box>

      {critical > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{critical} request{critical > 1 ? 's have' : ' has'} been waiting ≥5 days</strong> — action needed immediately.
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search name / summary / dept…" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Category" value={catFilter}
          onChange={e => setCatFilter(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(CATEGORY_CFG).map(([k,v]) => <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Type" value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Types</MenuItem>
          {uniqueTypes.map(([k,v]) => <MenuItem key={k} value={k}>{TYPE_EMOJI[k] ?? '📋'} {v}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Urgency" value={urgFilter}
          onChange={e => setUrgFilter(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="critical">🔴 Critical</MenuItem>
          <MenuItem value="high">🟡 High</MenuItem>
          <MenuItem value="normal">⚪ Normal</MenuItem>
        </TextField>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Inbox sx={{ fontSize: 80, opacity: 0.15, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {items.length === 0 ? 'All clear — no pending approvals 🎉' : 'No items match the current filters.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Urgency</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell align="right">Waiting</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(item => {
                const uc  = URGENCY_CFG[item.urgency];
                const cat = CATEGORY_CFG[item.category];
                const isProcessing = processing === item.id;
                return (
                  <TableRow key={`${item.type}-${item.id}`} hover
                    sx={{ bgcolor: item.urgency === 'critical' ? '#ffebee15' : item.urgency === 'high' ? '#fff3e015' : undefined }}>
                    <TableCell>
                      <Chip size="small" label={uc.label}
                        icon={uc.icon ? <Box sx={{ color: uc.color, display: 'flex' }}>{uc.icon}</Box> : undefined}
                        sx={{ bgcolor: uc.bg, color: uc.color, fontSize: '0.6rem' }} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small"
                        label={`${TYPE_EMOJI[item.type] ?? '📋'} ${item.typeLabel}`}
                        sx={{ bgcolor: (cat?.color ?? '#546e7a') + '15', color: cat?.color ?? '#546e7a', fontSize: '0.6rem' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{item.employeeName || '—'}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{item.deptName}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.summary}</Typography>
                      {item.detail && <Typography variant="caption" color="text.secondary">{item.detail}</Typography>}
                    </TableCell>
                    <TableCell>
                      {item.amount ? (
                        <Typography variant="body2" fontWeight={600} color="#2e7d32">
                          {item.currency ?? 'HKD'} {item.amount.toLocaleString('en-HK', { maximumFractionDigits: 0 })}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Chip size="small" label={`${item.daysWaiting}d`}
                        sx={{
                          bgcolor: uc.bg, color: uc.color, fontWeight: 700, fontSize: '0.65rem',
                        }} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Approve">
                          <span>
                            <IconButton size="small" color="success" disabled={isProcessing}
                              onClick={() => approve(item)}
                              sx={{ bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' } }}>
                              {isProcessing ? <CircularProgress size={16} /> : <Check fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <span>
                            <IconButton size="small" color="error" disabled={isProcessing}
                              onClick={() => { setRejectDialog(item); setRejectNote(''); }}
                              sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                              <Close fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject — {rejectDialog?.typeLabel}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {rejectDialog?.employeeName}: <strong>{rejectDialog?.summary}</strong>
          </Typography>
          <TextField fullWidth label="Reason for rejection" multiline rows={3}
            value={rejectNote} onChange={e => setRejectNote(e.target.value)}
            placeholder="Please provide a reason…" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={reject} disabled={!rejectNote.trim()}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
